import React, { useEffect, useState } from "react";
import { api, streamRetrain } from "../lib/api";
import { Brain, TrendingUp, TrendingDown, Activity, Loader2, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";

const STATUS_META = {
  winning: { color: "text-bm-green", icon: TrendingUp },
  watching: { color: "text-bm-amber", icon: Activity },
  losing: { color: "text-bm-danger", icon: TrendingDown },
};

function DeltaRow({ label, value, testId }) {
  const Icon = value > 0 ? ArrowUp : value < 0 ? ArrowDown : null;
  const color = value > 0 ? "text-bm-green" : value < 0 ? "text-bm-danger" : "text-bm-dim";
  return (
    <div className="flex items-center justify-between text-xs font-mono py-1" data-testid={testId}>
      <span className="text-bm-muted">{label}</span>
      <span className={`flex items-center gap-1 ${color}`}>
        {Icon && <Icon className="w-3 h-3" />}{value > 0 ? "+" : ""}{value}
      </span>
    </div>
  );
}

export default function Retraining() {
  const [launches, setLaunches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stat, setStat] = useState(null);
  const [aiText, setAiText] = useState("");
  const [aiStreaming, setAiStreaming] = useState(false);
  const [statBusy, setStatBusy] = useState(false);

  useEffect(() => {
    api.launches().then((d) => {
      setLaunches(d);
      if (d.length && !selected) setSelected(d[0]);
    });
    // Mount-only fetch; `selected` is intentionally read once to seed default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runStatistical = async () => {
    if (!selected) return;
    setStatBusy(true);
    try {
      const r = await api.retrainStat(selected.id);
      setStat(r);
    } finally {
      setStatBusy(false);
    }
  };

  const runAI = async () => {
    if (!selected || aiStreaming) return;
    setAiText("");
    setAiStreaming(true);
    try {
      for await (const ev of streamRetrain(selected.id)) {
        if (ev.type === "delta") setAiText((t) => t + ev.content);
        else if (ev.type === "error") setAiText((t) => t + `\n[error] ${ev.content}`);
      }
    } finally {
      setAiStreaming(false);
    }
  };

  // Auto-run statistical when selection changes
  useEffect(() => {
    setStat(null);
    setAiText("");
    if (selected) runStatistical();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">RETRAINING LOOP</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          Outcome feeds back.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-3xl">
          Each post-launch SKU adjusts validation-tree weights via two parallel reasoners — deterministic statistical mapping on the left, Claude AI explanation on the right.
        </p>
      </div>

      {/* Launch picker */}
      <div className="bm-card p-3 flex flex-wrap gap-2" data-testid="launch-picker">
        {launches.map((l) => {
          const meta = STATUS_META[l.status] || STATUS_META.watching;
          const Icon = meta.icon;
          const active = selected?.id === l.id;
          return (
            <button key={l.id} onClick={() => setSelected(l)}
              data-testid={`launch-pick-${l.id}`}
              className={`px-3 py-2 text-xs font-mono border flex items-center gap-2 transition-colors ${
                active ? "bg-bm-amber text-black border-bm-amber" : `border-white/10 ${meta.color} hover:border-white/30`
              }`}>
              <Icon className="w-3 h-3" />{l.name} · {l.repeat_rate}%
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* STATISTICAL */}
          <div className="bm-card p-5" data-testid="stat-panel">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-bm-cyan" />
                <span className="label-mono text-bm-cyan">STATISTICAL RETRAINER</span>
              </div>
              <button onClick={runStatistical} disabled={statBusy} data-testid="stat-rerun-btn"
                className="px-2 py-1 text-xs font-mono border border-white/10 hover:border-bm-cyan text-bm-muted hover:text-bm-cyan flex items-center gap-1">
                {statBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                RECOMPUTE
              </button>
            </div>

            {!stat && <div className="font-mono text-xs text-bm-muted">Computing deltas...</div>}
            {stat && (
              <>
                <div className="space-y-1 mb-3">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-bm-muted">Launch outcome score</span>
                    <span className="text-bm-green font-bold">{stat.outcome_score}/100</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-bm-muted">Direction × magnitude</span>
                    <span className={stat.direction > 0 ? "text-bm-green" : stat.direction < 0 ? "text-bm-danger" : "text-bm-dim"}>
                      {stat.direction > 0 ? "REINFORCE" : stat.direction < 0 ? "DAMPEN" : "HOLD"} ×{stat.magnitude}
                    </span>
                  </div>
                </div>

                <div className="border-t border-white/5 pt-3">
                  <div className="label-mono text-bm-dim mb-2">TREE WEIGHT ADJUSTMENTS</div>
                  {Object.entries(stat.tree_adjustments).map(([k, v]) => (
                    <DeltaRow key={k} label={k} value={v} testId={`adj-${k}`} />
                  ))}
                </div>

                {stat.node_hits.length > 0 && (
                  <div className="border-t border-white/5 pt-3 mt-3">
                    <div className="label-mono text-bm-dim mb-2">NODE-LEVEL HITS</div>
                    <div className="space-y-2">
                      {stat.node_hits.map((h, i) => (
                        <div key={`${h.tree}-${h.node}-${i}`} className="border-l-2 border-bm-cyan/40 pl-2 py-1" data-testid={`node-hit-${i}`}>
                          <div className="flex items-center justify-between text-xs font-mono">
                            <span className="text-white">{h.tree} / {h.node}</span>
                            <span className={h.delta > 0 ? "text-bm-green" : "text-bm-danger"}>{h.delta > 0 ? "+" : ""}{h.delta}</span>
                          </div>
                          <div className="text-[11px] text-bm-muted mt-0.5 leading-relaxed">{h.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-white/5 text-xs font-mono text-bm-muted leading-relaxed" data-testid="stat-summary">
                  {stat.summary}
                </div>
              </>
            )}
          </div>

          {/* AI-DRIVEN */}
          <div className="bm-card p-5" data-testid="ai-panel">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-bm-amber" />
                <span className="label-mono text-bm-amber">CLAUDE RETRAINING ORACLE</span>
              </div>
              <button onClick={runAI} disabled={aiStreaming} data-testid="ai-run-btn"
                className="px-2 py-1 text-xs font-mono border border-bm-amber/40 hover:bg-bm-amber hover:text-black text-bm-amber flex items-center gap-1 disabled:opacity-50">
                {aiStreaming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                {aiText ? "RE-RUN" : "RUN ANALYSIS"}
              </button>
            </div>
            {!aiText && !aiStreaming && (
              <div className="font-mono text-xs text-bm-muted leading-relaxed">
                Claude reads the launch outcome + original opportunity memo and proposes node-level weight adjustments with causal reasoning. Click <span className="text-bm-amber">RUN ANALYSIS</span> to stream.
              </div>
            )}
            {(aiText || aiStreaming) && (
              <div className="font-mono text-xs whitespace-pre-wrap text-white/90 leading-relaxed" data-testid="ai-output">
                {aiText}
                {aiStreaming && <span className="inline-block w-2 h-3 bg-bm-amber animate-pulse ml-1"></span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
